import { Component, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { Router } from '@angular/router';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { MapComponent } from '@components/entidades/common/map/map.component';
import { SignoLocalidad } from 'src/app/interfaces/interface-bakend/entidades/signo-localidad.interface';
import { SignoLocalidadService } from 'src/app/services/entidades/signo-localidades.service';
import { SignoCentroMedico } from 'src/app/interfaces/interface-bakend/entidades/signo-centro-medico.interface';
import { SignoCentroMedicoService } from 'src/app/services/entidades/signo-centros-medicos.service';
import { Catalogo } from 'src/app/interfaces/interface-bakend/shared/catalogo/catalogo.interface';
import { CatalogoService } from 'src/app/services/shared/catalogo.service';
import { EntidadesKeys } from 'src/app/enum/entidades-keys.enum';
import { GuiMsjService } from 'src/app/services/shared/gui-msj.service';
import { SignoPlan } from '@interfaces/interface-bakend/entidades/signo-plan.interface';
import { SignoPlanService } from '@services/entidades/signo-planes.service';
import { environment } from '@env/environment';

@Component({
  selector: 'app-centro-medico-alta',
  templateUrl: './centro-medico-alta.component.html',
  styleUrls: ['./centro-medico-alta.component.scss']
})
export class CentroMedicoAltaComponent implements OnInit, AfterViewInit {
  form: FormGroup;
  zonas: Catalogo[];
  localidades: SignoLocalidad[];
  rubros: Catalogo[];
  especialidades: Catalogo[];
  planes: SignoPlan[];

  // Para especialidades con chips
  especialidadesSeleccionadas: Catalogo[] = [];
  especialidadControl = new FormControl('');
  filteredEspecialidades: Catalogo[] = [];

  // Para rubros con chips
  rubrosSeleccionados: Catalogo[] = [];
  rubroControl = new FormControl('');
  filteredRubros: Catalogo[] = [];

  // Separadores para chips (Enter y Coma)
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];

  telefonosList: string[] = [];
  celularesList: string[] = [];
  inputTelefonoCelular: string = '';

  // Propiedades para autocompletado de calle
  calleControl = new FormControl('');
  calleSuggestions: any[] = [];
  private calleSearchTerms = new Subject<string>();

  // Almacena la última sugerencia seleccionada para usarla en la búsqueda
  selectedAddressFeature: any = null;

  @ViewChild(MapComponent) mapComponent: MapComponent;

  constructor(
    private signoCentroMedicoService: SignoCentroMedicoService,
    private catalogoService: CatalogoService,
    private signoLocalidadService: SignoLocalidadService,
    private router: Router,
    private guiMsjService: GuiMsjService,
    private signoPlanService: SignoPlanService
  ) {
    this.getZonas();
    this.getEspecialidades();
    this.getRubros();
    this.getPlanes();

    // Configurar búsqueda con debounce para la calle
    this.calleSearchTerms.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((term: string) => this.searchCalleSuggestions(term))
    ).subscribe(suggestions => {
      this.calleSuggestions = suggestions;
    });
  }

  ngOnInit(): void {
    this.createForm();
    this.initAutocompleteFilters();
  }

  ngAfterViewInit(): void {
    this.initMap();
    this.updateMap();
  }

  private initAutocompleteFilters(): void {
    // Filtrar especialidades según el texto ingresado
    this.especialidadControl.valueChanges.pipe(
      debounceTime(200),
      distinctUntilChanged()
    ).subscribe(value => {
      this.filterEspecialidades(typeof value === 'string' ? value : '');
    });

    // Filtrar rubros según el texto ingresado
    this.rubroControl.valueChanges.pipe(
      debounceTime(200),
      distinctUntilChanged()
    ).subscribe(value => {
      this.filterRubros(typeof value === 'string' ? value : '');
    });
  }

  get centroMedicoNoValido() {
    return this.form.get('nombre').invalid && this.form.get('nombre').touched;
  }

  get calleNoValida() {
    return this.form.get('calle').invalid && this.form.get('calle').touched;
  }

  get alturaNoValida() {
    return this.form.get('altura').invalid && this.form.get('altura').touched;
  }

  get localidadNoValida() {
    return this.form.get('localidad').invalid && this.form.get('localidad').touched;
  }

  get zonaNoValida() {
    return this.form.get('zona').invalid && this.form.get('zona').touched;
  }

  get planesNoValidos() {
    return this.form.get('planes').invalid && this.form.get('planes').touched;
  }

  async getEspecialidades() {
    try {
      const especialidades = await this.catalogoService.findDescendantsByKey(EntidadesKeys.ESPECIALIDADES);
      if (especialidades) {
        this.especialidades = especialidades;
        this.filteredEspecialidades = especialidades;
      }
    } catch (error) {
      console.error("Error al cargar las especialidades", error);
    }
  }

  async getZonas() {
    try {
      const zonas = await this.catalogoService.findDescendantsByKey(EntidadesKeys.ZONAS);
      if (zonas) {
        this.zonas = zonas;
      }
    } catch (error) {
      console.error("Error al cargar las zonas", error);
    }
  }

  async getPlanes() {
    try {
      const planes = await this.signoPlanService.getPlanes();
      if (planes) {
        this.planes = planes;
      }
    } catch (error) {
      console.error("Error al cargar los planes", error);
    }
  }

  async getRubros() {
    try {
      const rubros = await this.catalogoService.findDescendantsByKey(EntidadesKeys.RUBROS);
      if (rubros) {
        this.rubros = rubros;
        this.filteredRubros = rubros;
      }
    } catch (error) {
      console.error("Error al cargar los rubros", error);
    }
  }

  async getLocalidades() {
    try {
      const localidades = await this.signoLocalidadService.getLocalidades();
      if (localidades) {
        this.localidades = localidades;
      }
    } catch (error) {
      console.error("Error al cargar las localidades", error);
    }
  }

  async onZonaChange() {
    const zonaId = this.form && this.form.get('zona') ? this.form.get('zona').value : '';
    this.form.get('localidad').setValue('');
    if (!zonaId) {
      this.localidades = [];
      return;
    }
    try {
      this.localidades = await this.signoLocalidadService.listarLocalidadesByZona(zonaId);
    } catch (error) {
      console.error('Error al cargar las localidades de la zona', error);
      this.localidades = [];
    }
  }

  async getLocalidadSeleccionada(_id: string): Promise<SignoLocalidad> {
    try {
      const localidad = await this.signoLocalidadService.getLocalidadById(_id);
      if (localidad) {
        const localidadFiltrada: SignoLocalidad = {
          "_id": localidad?._id,
          "nombre": localidad?.nombre,
          "activo": localidad?.activo,
          "zona": localidad?.zona
        };
        return localidadFiltrada;
      }
    } catch (error) {
      console.error("Error al obtener la localidad seleccionada", error);
    }
  }

  // Inicializa el mapa con las coordenadas del formulario
  initMap() {
    const lat = this.form.get('latitud')?.value;
    const lng = this.form.get('longitud')?.value;
    if (lat != null && lng != null && !isNaN(Number(lat)) && !isNaN(Number(lng))) {
      this.mapComponent.setLocation(Number(lat), Number(lng));
    }
  }

  // Actualiza el mapa cuando cambian las coordenadas
  updateMap() {
    this.updatesCoordinates();
    const lat = this.form.get('latitud')?.value;
    const lng = this.form.get('longitud')?.value;
    if (lat != null && lng != null && !isNaN(Number(lat)) && !isNaN(Number(lng))) {
      this.mapComponent.setLocation(Number(lat), Number(lng));
    }
  }

  // Escucha los cambios de coordenadas desde el mapa (arrastre del marcador)
  updatesCoordinates() {
    this.mapComponent.coordinatesChanged.subscribe((coordinates) => {
      console.log('Coordenadas actualizadas desde el mapa:', coordinates);
      this.form.patchValue({
        latitud: coordinates.latitud,
        longitud: coordinates.longitud
      });
    });
  }

  // Actualiza la ubicación del marcador cuando se modifican manualmente los campos de coordenadas (aunque estén ocultos)
  updateMarkerLocation() {
    const lat = this.form.get('latitud')?.value;
    const lng = this.form.get('longitud')?.value;
    if (this.mapComponent && lat != null && lng != null && !isNaN(Number(lat)) && !isNaN(Number(lng))) {
      this.mapComponent.setLocation(Number(lat), Number(lng));
    }
  }

  createForm() {
    this.form = new FormGroup({
      nombre: new FormControl('', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(50),
      ]),
      activo: new FormControl(false),
      calle: new FormControl('', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(50),
      ]),
      altura: new FormControl('', [
        Validators.required,
        Validators.minLength(1),
        Validators.maxLength(50),
      ]),
      pisoDpto: new FormControl('', [
        Validators.minLength(1),
        Validators.maxLength(50),
      ]),
      telefonos: new FormControl(''),
      celular: new FormControl(''),
      horarios: new FormControl('', [
        Validators.minLength(3),
        Validators.maxLength(50),
      ]),
      zona: new FormControl('', [
        Validators.required,
      ]),
      localidad: new FormControl('', [
        Validators.required,
      ]),
      latitud: new FormControl(-34.63104776379445, [
        Validators.pattern(/^-?\d*(\.\d+)?$/)]),
      longitud: new FormControl(-58.46651124133983, [
        Validators.pattern(/^-?\d*(\.\d+)?$/)]),
      rubros: new FormControl([]),
      especialidades: new FormControl([]),
      planes: new FormControl([], [
        Validators.required,
        Validators.minLength(1)
      ]),
    }, { validators: this.validarAlMenosUnContacto() });
  }

  validarAlMenosUnContacto(): ValidatorFn {
    return (formGroup: AbstractControl): { [key: string]: any } | null => {
      if (this.telefonosList.length > 0 || this.celularesList.length > 0) {
        return null;
      }
      return { atLeastOneRequired: true };
    };
  }

  // --- Métodos para Especialidades (chips + autocomplete) ---
  addEspecialidad(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (!value) return;

    const especialidad = this.especialidades.find(e => e.concepto.toLowerCase() === value.toLowerCase());
    if (especialidad && !this.especialidadesSeleccionadas.some(e => e._id === especialidad._id)) {
      this.especialidadesSeleccionadas.push(especialidad);
      this.updateFormEspecialidades();
    }

    // Limpiar el input (compatible con diferentes versiones)
    const input = event.input;
    if (input) {
      input.value = '';
    }
    this.especialidadControl.setValue('');
  }

  addRubro(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (!value) return;
    const rubro = this.rubros.find(r => r.concepto.toLowerCase() === value.toLowerCase());
    if (rubro && !this.rubrosSeleccionados.some(r => r._id === rubro._id)) {
      this.rubrosSeleccionados.push(rubro);
      this.updateFormRubros();
    }
    const input = event.input;
    if (input) {
      input.value = '';
    }
    this.rubroControl.setValue('');
  }

  selectedEspecialidad(event: MatAutocompleteSelectedEvent): void {
    const id = event.option.value;
    const especialidad = this.especialidades.find(e => e._id === id);
    if (especialidad && !this.especialidadesSeleccionadas.some(e => e._id === id)) {
      this.especialidadesSeleccionadas.push(especialidad);
      this.updateFormEspecialidades();
    }
    this.especialidadControl.setValue('');
  }

  removeEspecialidad(id: string | undefined): void {
    if (!id) return;
    const index = this.especialidadesSeleccionadas.findIndex(e => e._id === id);
    if (index >= 0) {
      this.especialidadesSeleccionadas.splice(index, 1);
      this.updateFormEspecialidades();
    }
  }

  private updateFormEspecialidades(): void {
    const ids = this.especialidadesSeleccionadas.map(e => e._id);
    this.form.patchValue({ especialidades: ids });
    this.filterEspecialidades(this.especialidadControl.value || '');
  }

  filterEspecialidades(query: string): void {
    const filterValue = query.toLowerCase();
    this.filteredEspecialidades = this.especialidades.filter(especialidad =>
      especialidad.concepto.toLowerCase().includes(filterValue) &&
      !this.especialidadesSeleccionadas.some(sel => sel._id === especialidad._id)
    );
  }

  selectedRubro(event: MatAutocompleteSelectedEvent): void {
    const id = event.option.value;
    const rubro = this.rubros.find(r => r._id === id);
    if (rubro && !this.rubrosSeleccionados.some(r => r._id === id)) {
      this.rubrosSeleccionados.push(rubro);
      this.updateFormRubros();
    }
    this.rubroControl.setValue('');
  }

  removeRubro(id: string | undefined): void {
    if (!id) return;
    const index = this.rubrosSeleccionados.findIndex(r => r._id === id);
    if (index >= 0) {
      this.rubrosSeleccionados.splice(index, 1);
      this.updateFormRubros();
    }
  }

  private updateFormRubros(): void {
    const ids = this.rubrosSeleccionados.map(r => r._id);
    this.form.patchValue({ rubros: ids });
    this.filterRubros(this.rubroControl.value || '');
  }

  filterRubros(query: string): void {
    const filterValue = query.toLowerCase();
    this.filteredRubros = this.rubros.filter(rubro =>
      rubro.concepto.toLowerCase().includes(filterValue) &&
      !this.rubrosSeleccionados.some(sel => sel._id === rubro._id)
    );
  }

  // --- Gestión de teléfonos/celulares (sin cambios) ---
  agregarTelefonoCelular(tipo: 'telefono' | 'celular') {
    const valor = this.inputTelefonoCelular.trim();
    if (valor) {
      if (tipo === 'telefono') {
        this.telefonosList.push(valor);
      } else {
        this.celularesList.push(valor);
      }
      this.inputTelefonoCelular = '';
      this.form.get('telefonos').markAsTouched();
      this.form.get('celular').markAsTouched();
      this.form.updateValueAndValidity();
    }
  }

  eliminarTelefono(index: number) {
    this.telefonosList.splice(index, 1);
    this.form.get('telefonos').markAsTouched();
    this.form.updateValueAndValidity();
  }

  eliminarCelular(index: number) {
    this.celularesList.splice(index, 1);
    this.form.get('celular').markAsTouched();
    this.form.updateValueAndValidity();
  }

  // --- Métodos para autocompletado de calle ---
  filterCalleKeydown(event: KeyboardEvent) {
    // Permite números y texto en la calle
  }

  onCalleInput() {
    const value = this.calleControl.value || '';
    this.form.patchValue({ calle: value });
    this.calleSearchTerms.next(value);
  }

  private searchCalleSuggestions(query: string): Promise<any[]> {
    if (!query || query.length < 3) {
      return Promise.resolve([]);
    }
    const token = environment.mapBoxToken;
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${token}&autocomplete=true&types=address&country=ar&limit=5`;
    return fetch(url)
      .then(response => response.json())
      .then(data => data.features || [])
      .catch(error => {
        console.error('Error fetching suggestions', error);
        return [];
      });
  }

  onCalleSelected(event: MatAutocompleteSelectedEvent) {
    const feature = event.option.value;
    if (!feature) return;

    this.selectedAddressFeature = feature;

    const calle = this.displayCalleFn(feature);

    this.form.patchValue({ calle });
    setTimeout(() => {
      this.calleControl.setValue(calle, { emitEvent: false });
    }, 0);

    if (this.form.get('altura')?.value) {
      this.buscarDireccionCompleta();
    }
  }

  displayCalleFn = (suggestion: any): string => {
    if (!suggestion) return '';
    if (typeof suggestion === 'string') return suggestion;
    if (suggestion.text) return suggestion.text;
    const primeraParte = suggestion.place_name ? suggestion.place_name.split(',')[0].trim() : '';
    const partes = primeraParte.split(' ');
    const ultimo = partes[partes.length - 1];
    if (/^\d+$/.test(ultimo) && partes.length > 1) {
      return partes.slice(0, -1).join(' ');
    }
    return primeraParte;
  };

  // --- Búsqueda completa (calle + altura) usando la sugerencia almacenada ---
  buscarDireccionCompleta() {
    const calle = this.form.get('calle')?.value;
    const altura = this.form.get('altura')?.value;
    if (!calle || !altura) {
      alert('Debe ingresar calle y altura para buscar.');
      return;
    }

    // Si tenemos un feature almacenado de la sugerencia seleccionada
    if (this.selectedAddressFeature) {
      console.log('Usando feature almacenado:', this.selectedAddressFeature);

      // Extraer la localidad del contexto para mejorar la geocodificación
      let localidad = '';
      if (this.selectedAddressFeature.context) {
        // Buscar el elemento de tipo 'place' (ciudad/localidad)
        const placeContext = this.selectedAddressFeature.context.find((ctx: any) => ctx.id.startsWith('place.'));
        if (placeContext) {
          localidad = placeContext.text;
        }
      }

      const direccionCompleta = localidad
        ? `${calle} ${altura}, ${localidad}, Argentina`
        : `${calle} ${altura}, Argentina`;

      console.log('Dirección a geocodificar (con localidad extraída):', direccionCompleta);
      this.mapComponent.searchFromAddress(direccionCompleta)
        .then(() => {
          console.log('Mapa actualizado con la dirección:', direccionCompleta);
        })
        .catch(error => {
          console.error('Error al buscar dirección:', error);
          alert('No se pudo encontrar la dirección. Intente nuevamente.');
        });
    } else {
      // Sin feature almacenado, usar el método anterior (menos preciso)
      const direccionCompleta = `${calle} ${altura}, Argentina`;
      console.log('Sin feature almacenado, usando dirección simple:', direccionCompleta);
      this.mapComponent.searchFromAddress(direccionCompleta)
        .then(() => {
          console.log('Mapa actualizado con la dirección:', direccionCompleta);
        })
        .catch(error => {
          console.error('Error al buscar dirección:', error);
          alert('No se pudo encontrar la dirección. Intente nuevamente.');
        });
    }
  }

  async createCentroMedico() {
    if (this.form.valid) {
      await this.guiMsjService.msjFormSubmit('Guardando');
      const centroMedico = await this.setDataForma();
      console.log('Datos a enviar al backend:', centroMedico);
      this.signoCentroMedicoService.create(centroMedico);
      await this.guiMsjService.msjFormSubmit('DatosGuardados');
      this.router.navigateByUrl('/centros-medicos-listado');
    } else {
      this.form.markAllAsTouched();
      if (this.form.hasError('atLeastOneRequired')) {
        console.log('Error de validación: Para guardar, ingrese al menos un teléfono o celular.');
      } else {
        console.log('Error de validación: Formulario inválido. Por favor, revise los campos.');
      }
    }
  }

  async setDataForma(): Promise<SignoCentroMedico> {
    const especaialidadesObjectId = this.form.value.especialidades.map((especialidadId: string) => ({ _id: especialidadId }));
    const rubrosObjectId = this.form.value.rubros.map((rubroId: string) => ({ _id: rubroId }));
    const localidad = await this.getLocalidadSeleccionada(this.form.value.localidad);
    const planesObjectId = this.form.value.planes.map((planesId: string) => ({ _id: planesId }));

    const telefonosConcatenados = this.telefonosList.join(', ');
    const celularesConcatenados = this.celularesList.join(', ');

    const centroMedico: SignoCentroMedico = {
      nombre: this.form.value.nombre || '',
      activo: this.form.value.activo || false,
      calle: this.form.value.calle || '',
      altura: this.form.value.altura || '',
      pisoDpto: this.form.value.pisoDpto || '',
      telefonos: telefonosConcatenados,
      celular: celularesConcatenados,
      zona: {
        _id: this.form.value.zona,
      },
      localidad: localidad,
      horarios: this.form.value.horarios || '',
      longitud: Number(this.form.value.longitud) || 0.0,
      latitud: Number(this.form.value.latitud) || 0.0,
      rubros: rubrosObjectId,
      especialidades: especaialidadesObjectId,
      planes: planesObjectId,
    };

    return centroMedico;
  }

  // Método auxiliar (sin cambios)
  private extraerIds(arr: any[]): string[] {
    if (!arr) return [];
    return arr.map(item => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object' && item._id) return item._id;
      return null;
    }).filter(id => id !== null);
  }
}